import {
  managementUserForgotPassword,
  managementUserGet,
  managementUserLoginEmail,
  managementUserResetPassword,
} from '@prosaas/psw-api-utils';
import {
  AUTH_PERMISSIONS,
  AUTH_REQUEST_LOGOUT,
  AUTH_SESSION,
  AUTH_USER_ID,
  AUTH_USER_TYPE,
  AUTH_USERNAME,
} from '@prosaas/psw-events';
import {
  emit,
  getLocalItem,
  handleGuardedErrors,
  removeLocalItem,
  setLocalItem,
  subscribe,
} from '@prosaas/psw-utils';
import TagManager from 'react-gtm-module';
import type {
  IApiError,
  ILogInEmailRequest,
  ILogInResponse,
  IPasswordResetRequest,
  IUserGetResponse,
  NullishString,
} from '../utils/interfaces';
import { navigateToUrl } from 'single-spa';
import { forgotPasswordRoute, homeRoute, resetPasswordRoute } from '../utils/routes';

export const SESSION_ID = 'sessionId';

let sid: NullishString = null;

const routesAllowedWhenLoggedOut = [homeRoute(), forgotPasswordRoute(), resetPasswordRoute()];

export const apiService = {
  async logInWithEmail(credentials: ILogInEmailRequest): Promise<void> {
    const logInResponse: ILogInResponse = await managementUserLoginEmail({ data: credentials }).catch(
      handleGuardedErrors,
    );

    sid = logInResponse.token;

    setLocalItem(SESSION_ID, sid);
    emit(AUTH_SESSION, { isLoggedIn: true, sessionId: sid });

    apiService.getOwnData();
  },
  async getOwnData(): Promise<IUserGetResponse> {
    const userResponse: IUserGetResponse = await managementUserGet({ sid }).catch(handleGuardedErrors);

    const { name, userType, permissions, id } = userResponse;

    emit(AUTH_USERNAME, name);
    emit(AUTH_USER_TYPE, userType);
    emit(AUTH_PERMISSIONS, permissions);
    emit(AUTH_USER_ID, id);
    TagManager.dataLayer({
      dataLayer: {
        event: 'userData',
        userData: { userId: userResponse.id, userType: userResponse.userType.name },
      },
    });
    return userResponse;
  },
  async logOut(): Promise<void> {
    removeLocalItem(SESSION_ID);

    sid = null;

    emit(AUTH_SESSION, { isLoggedIn: false });
    emit(AUTH_USERNAME, null);
    emit(AUTH_PERMISSIONS, null);
    emit(AUTH_USER_ID, null);

    if (!routesAllowedWhenLoggedOut.includes(window.location.pathname)) {
      navigateToUrl(homeRoute());
    }
  },
  forgottenPassword(email: string): Promise<void> {
    return managementUserForgotPassword({ data: { email } }).catch(handleGuardedErrors);
  },
  resetPassword(data: IPasswordResetRequest): Promise<void> {
    return managementUserResetPassword({ data }).catch(handleGuardedErrors);
  },
};

init();

subscribe(AUTH_REQUEST_LOGOUT, apiService.logOut, { skipReplay: true });

function init() {
  const sessionId = getLocalItem(SESSION_ID);

  if (!sessionId) {
    apiService.logOut();
    return;
  }

  sid = sessionId;
  emit(AUTH_SESSION, { isLoggedIn: true, sessionId });

  apiService.getOwnData().catch((err: IApiError) => {
    if (err?.message === 'UNAUTHORIZED') {
      apiService.logOut();
    }
  });
}
